<template>
  <div class="mt-3 mb-5">

    <!-- ── Filters ── -->
    <div class="admin-card mb-3">
      <div class="card-body py-2 px-3">
        <div class="row align-items-center">

          <!-- Search -->
          <div class="col-12 col-md-3 mb-2 mb-md-0">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text bg-white border-right-0">
                  <i class="bi bi-search text-secondary"></i>
                </span>
              </div>
              <input
                v-model="filters.search"
                @input="debouncedFetch"
                class="form-control border-left-0"
                placeholder="Search name, email, phone..."
              />
            </div>
          </div>

          <!-- Status -->
          <div class="col-6 col-md-2 mb-2 mb-md-0">
            <select v-model="filters.status" @change="fetchCompanies(1)" class="form-control">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="inactive">Inactive</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <!-- Sort -->
          <div class="col-6 col-md-2 mb-2 mb-md-0">
            <select v-model="filters.sort" @change="fetchCompanies(1)" class="form-control">
              <optgroup label="Join date">
                <option value="created_at:desc">Newest joined</option>
                <option value="created_at:asc">Oldest joined</option>
              </optgroup>
              <optgroup label="Activity">
                <option value="updated_at:desc">Recently active</option>
                <option value="updated_at:asc">Least active</option>
              </optgroup>
              <optgroup label="Name">
                <option value="name:asc">Name A → Z</option>
                <option value="name:desc">Name Z → A</option>
              </optgroup>
            </select>
          </div>

          <!-- Show test -->
          <div class="col-6 col-md-3 mb-2 mb-md-0 d-flex align-items-center" style="gap:16px;">
            <div class="custom-control custom-switch ml-1">
              <input
                type="checkbox"
                class="custom-control-input"
                id="showTestSwitch"
                v-model="filters.show_test"
                @change="fetchCompanies(1)"
              />
              <label class="custom-control-label" for="showTestSwitch">Show test</label>
            </div>
          </div>

          <!-- Count + export -->
          <div class="col-6 col-md-2 text-right d-flex align-items-center justify-content-end">
            <small class="text-secondary mr-2">{{ meta.total || 0 }} companies</small>
            <button class="btn btn-outline-secondary" @click="exportCsv" :disabled="exporting" title="Export CSV">
              <span v-if="exporting" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bi bi-download"></i>
            </button>
          </div>

        </div>
      </div>
    </div>

    <!-- ── Bulk toolbar ── -->
    <div v-if="selectedIds.length > 0" class="admin-card mb-2" style="background:#f8fafc;">
      <div class="card-body py-2 px-3 d-flex align-items-center flex-wrap" style="gap:8px;">
        <span class="font-weight-bold text-primary" style="font-size:.85rem;">{{ selectedIds.length }} selected</span>
        <button v-if="can('dukakit.send-notifications')" class="btn btn-outline-primary" @click="bulkNotify" :disabled="bulkLoading">
          <span v-if="bulkLoading === 'notify'" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="bi bi-bell mr-1"></i> Notify All
        </button>
        <button class="btn btn-outline-secondary" @click="bulkExport">
          <i class="bi bi-download mr-1"></i> Export Selected
        </button>
        <button v-if="can('dukakit.archive-company')" class="btn btn-outline-danger" @click="bulkArchive" :disabled="bulkLoading">
          <span v-if="bulkLoading === 'archive'" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="bi bi-archive mr-1"></i> Archive All
        </button>
        <button class="btn btn-link text-secondary" @click="clearSelection">Clear</button>
      </div>
    </div>

    <!-- ── Bulk notify modal ── -->
    <b-modal
      v-model="showBulkNotifyModal"
      :title="`Send Notification to ${selectedIds.length} Merchants`"
      centered
    >
      <textarea
        v-model="bulkMessage"
        class="form-control"
        rows="3"
        placeholder="Message to send to all selected merchants…"
        maxlength="500"
      ></textarea>
      <small class="text-secondary d-block mt-1">{{ bulkMessage.length }}/500</small>
      <template #modal-footer>
        <button class="btn btn-secondary" @click="showBulkNotifyModal = false">Cancel</button>
        <button class="btn btn-primary" @click="submitBulkNotify" :disabled="!bulkMessage.trim() || bulkLoading">
          <span v-if="bulkLoading" class="spinner-border spinner-border-sm mr-1"></span>
          Send
        </button>
      </template>
    </b-modal>

    <!-- ── Data table ── -->
    <div class="admin-card">
      <b-table
        :items="companies"
        :fields="tableFields"
        :busy="loading"
        :tbody-tr-class="rowClass"
        hover
        responsive
        show-empty
        no-local-sorting
        table-class="mb-0"
        thead-class="thead-light"
        @row-clicked="onRowClicked"
      >

        <!-- Loading -->
        <template #table-busy>
          <div class="text-center py-5">
            <div class="spinner-border text-secondary" role="status"></div>
          </div>
        </template>

        <!-- Empty -->
        <template #empty>
          <div class="text-center text-secondary py-5">
            <i class="bi bi-buildings" style="font-size:2rem;"></i>
            <p class="mt-2 mb-0">No companies found</p>
          </div>
        </template>

        <!-- ── Select all header ── -->
        <template #head(select)>
          <input
            type="checkbox"
            :checked="allSelected"
            :indeterminate.prop="someSelected && !allSelected"
            @change="toggleSelectAll"
            style="cursor:pointer;"
          />
        </template>

        <!-- ── Checkbox ── -->
        <template #cell(select)="{ item }">
          <div @click.stop>
            <input
              type="checkbox"
              :checked="selectedIds.includes(item.id)"
              @change="toggleSelect(item.id)"
              style="cursor:pointer;"
            />
          </div>
        </template>

        <!-- ── Avatar ── -->
        <template #cell(avatar)="{ item }">
          <div
            v-if="item.logo_url"
            class="rounded-circle border overflow-hidden"
            style="width:36px;height:36px;"
          >
            <img :src="item.logo_url" style="width:100%;height:100%;object-fit:cover;" />
          </div>
          <div
            v-else
            class="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold"
            :style="{ background: avatarColor(item.name), width:'36px', height:'36px', fontSize:'.72rem' }"
          >
            {{ initials(item.name) }}
          </div>
        </template>

        <!-- ── Company name + email ── -->
        <template #cell(company)="{ item }">
          <p class="mb-0 font-weight-bold" style="font-size:.9rem;">{{ item.name }}</p>
          <small class="text-secondary">{{ item.email }}</small>
        </template>

        <!-- ── Status ── -->
        <template #cell(status)="{ item }">
          <span :class="['badge', statusBadgeClass(item)]">{{ statusLabel(item) }}</span>
        </template>

        <!-- ── Plan ── -->
        <template #cell(plan)="{ item }">
          <small>{{ item.billing && item.billing.subscription_plan ? item.billing.subscription_plan.name : '—' }}</small>
        </template>

        <!-- ── Billing due ── -->
        <template #cell(billing_due)="{ item }">
          <small :class="isDuePast(item) ? 'text-danger font-weight-bold' : 'text-secondary'">
            {{ item.billing ? formatDate(item.billing.due_date) : '—' }}
          </small>
        </template>

        <!-- ── Last activity ── -->
        <template #cell(last_activity)="{ item }">
          <small class="text-secondary">
            {{ item.user_activity_summary ? fromNow(item.user_activity_summary.updated_at) : '—' }}
          </small>
        </template>

        <!-- ── Health ── -->
        <template #cell(health)="{ item }">
          <span :class="['badge', healthBadge(item).cls]" :title="'Score: ' + healthScore(item)">
            {{ healthBadge(item).label }}
          </span>
        </template>

        <!-- ── Onboarding ── -->
        <template #cell(onboarding)="{ item }">
          <template v-if="item.onboarding">
            <div class="d-flex align-items-center mb-1">
              <div style="flex:1;height:4px;background:#dee2e6;border-radius:99px;overflow:hidden;margin-right:6px;">
                <div
                  :style="{
                    width: item.onboarding.percentage + '%',
                    background: onboardingColor(item),
                    height: '100%',
                    borderRadius: '99px',
                  }"
                ></div>
              </div>
              <small :style="{ color: onboardingColor(item), fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' }">
                {{ item.onboarding.percentage }}%
              </small>
            </div>
            <small class="text-secondary d-block" style="font-size:0.65rem;line-height:1.2;">
              {{ item.onboarding.next_step || 'Complete' }}
            </small>
          </template>
          <small v-else class="text-secondary">—</small>
        </template>

        <!-- ── Actions ── -->
        <template #cell(actions)="{ item }">
          <div @click.stop class="text-right">
            <b-dropdown
              variant="link"
              no-caret
              right
              boundary="window"
              toggle-class="text-secondary p-1"
            >
              <template #button-content>
                <i class="bi bi-three-dots-vertical"></i>
              </template>

              <!-- View -->
              <b-dropdown-header style="font-size:.7rem;">View</b-dropdown-header>
              <b-dropdown-item @click="$emit('company-selected', item)">
                <i class="bi bi-eye mr-2"></i> View Details
              </b-dropdown-item>
              <b-dropdown-item v-if="can('dukakit.impersonate-company')" @click="$emit('impersonate', item)">
                <i class="bi bi-person-badge mr-2"></i> View as Owner
              </b-dropdown-item>
              <b-dropdown-item @click="copyEmail(item)">
                <i class="bi bi-clipboard mr-2"></i> Copy Owner Email
              </b-dropdown-item>

              <!-- Billing -->
              <b-dropdown-divider />
              <b-dropdown-header style="font-size:.7rem;">Billing</b-dropdown-header>
              <b-dropdown-item v-if="can('dukakit.manage-billing')" @click="$emit('billing-date', item)">
                <i class="bi bi-calendar-event mr-2"></i> Adjust Billing Date
              </b-dropdown-item>
              <b-dropdown-item v-if="can('dukakit.manage-billing')" @click="$emit('change-plan', item)">
                <i class="bi bi-arrow-repeat mr-2"></i> Change Plan
              </b-dropdown-item>
              <b-dropdown-item v-if="can('dukakit.extend-trial')" @click="$emit('extend-trial', item)">
                <i class="bi bi-hourglass-split mr-2"></i> Extend Trial
              </b-dropdown-item>
              <b-dropdown-item v-if="can('dukakit.waive-billing')" @click="$emit('waive-overdue', item)">
                <i class="bi bi-check-circle mr-2"></i> Waive Overdue
              </b-dropdown-item>
              <b-dropdown-item v-if="can('dukakit.extend-trial')" @click="$emit('convert-trial', item)">
                <i class="bi bi-lightning mr-2"></i> Convert Trial → Active
              </b-dropdown-item>

              <!-- Account -->
              <b-dropdown-divider />
              <b-dropdown-header style="font-size:.7rem;">Account</b-dropdown-header>
              <b-dropdown-item v-if="can('dukakit.manage-affiliates')" @click="$emit('assign-affiliate', item)">
                <i class="bi bi-people mr-2"></i> Assign Affiliate
              </b-dropdown-item>
              <b-dropdown-item @click="$emit('mark-test', item)">
                <i class="bi bi-tag mr-2"></i> {{ item.is_test ? 'Unmark Test' : 'Mark as Test' }}
              </b-dropdown-item>
              <b-dropdown-item v-if="can('dukakit.manage-notes')" @click="$emit('company-selected', item)">
                <i class="bi bi-sticky mr-2"></i> Add Note
              </b-dropdown-item>

              <!-- Comms -->
              <template v-if="can('dukakit.send-notifications')">
                <b-dropdown-divider />
                <b-dropdown-header style="font-size:.7rem;">Comms</b-dropdown-header>
                <b-dropdown-item @click="$emit('notify', item)">
                  <i class="bi bi-bell mr-2"></i> Send Notification
                </b-dropdown-item>
              </template>

              <!-- Security -->
              <template v-if="can('dukakit.manage-sessions') || can('dukakit.impersonate-company')">
                <b-dropdown-divider />
                <b-dropdown-header style="font-size:.7rem;">Security</b-dropdown-header>
                <b-dropdown-item v-if="can('dukakit.manage-sessions')" @click="$emit('force-logout', item)">
                  <i class="bi bi-shield-x mr-2"></i> Force Logout
                </b-dropdown-item>
                <b-dropdown-item v-if="can('dukakit.impersonate-company')" @click="$emit('password-reset', item)">
                  <i class="bi bi-key mr-2"></i> Send Password Reset
                </b-dropdown-item>
              </template>

              <!-- Danger -->
              <template v-if="can('dukakit.suspend-company') || can('dukakit.archive-company') || can('dukakit.delete-company') || can('dukakit.delete-inventory')">
                <b-dropdown-divider />
                <b-dropdown-header class="text-danger" style="font-size:.7rem;">Danger</b-dropdown-header>
                <b-dropdown-item v-if="can('dukakit.suspend-company')" link-class="text-danger" @click="$emit('reactivate', item)">
                  <i class="bi bi-arrow-counterclockwise mr-2"></i> Reactivate
                </b-dropdown-item>
                <b-dropdown-item v-if="can('dukakit.suspend-company')" link-class="text-danger" @click="$emit('suspend', item)">
                  <i class="bi bi-slash-circle mr-2"></i> Suspend
                </b-dropdown-item>
                <b-dropdown-item v-if="can('dukakit.archive-company')" link-class="text-danger" @click="$emit('archive', item)">
                  <i class="bi bi-archive mr-2"></i> Archive
                </b-dropdown-item>
                <b-dropdown-item v-if="can('dukakit.delete-inventory')" link-class="text-danger" @click="$emit('delete-inventory', item)">
                  <i class="bi bi-trash mr-2"></i> Delete Inventory
                </b-dropdown-item>
                <b-dropdown-item v-if="can('dukakit.delete-company')" link-class="text-danger font-weight-bold" @click="$emit('delete-company', item)">
                  <i class="bi bi-x-octagon mr-2"></i> Delete Company
                </b-dropdown-item>
              </template>

            </b-dropdown>
          </div>
        </template>

      </b-table>

      <!-- ── Pagination ── -->
      <div v-if="meta.last_page > 1" class="d-flex justify-content-between align-items-center px-3 py-2 border-top">
        <small class="text-secondary">Page {{ meta.current_page }} of {{ meta.last_page }}</small>
        <div>
          <button
            class="btn btn-outline-secondary mr-1"
            :disabled="meta.current_page <= 1"
            @click="fetchCompanies(meta.current_page - 1)"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <button
            class="btn btn-outline-secondary"
            :disabled="meta.current_page >= meta.last_page"
            @click="fetchCompanies(meta.current_page + 1)"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { dukakitCan } from '../../utils/dukakitCan';
import _ from 'lodash';

export default {
  name: 'AdminCompaniesTable',

  props: {
    dateRange: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      loading: false,
      companies: [],
      meta: {},
      debouncedFetch: null,
      exporting: false,
      selectedIds: [],
      bulkLoading: null,
      bulkMessage: '',
      showBulkNotifyModal: false,
      filters: {
        search: '',
        status: '',
        sort: 'created_at:desc',
        show_test: false,
      },
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    tableFields() {
      return [
        {
          key: 'select',
          label: '',
          thStyle: { width: '36px' },
          thClass: 'pl-3',
          tdClass: 'align-middle pl-3',
        },
        {
          key: 'avatar',
          label: '',
          thStyle: { width: '44px' },
          tdClass: 'align-middle',
        },
        {
          key: 'company',
          label: 'Company',
          tdClass: 'align-middle',
        },
        {
          key: 'status',
          label: 'Status',
          thClass: 'd-none d-md-table-cell',
          tdClass: 'align-middle d-none d-md-table-cell',
        },
        {
          key: 'plan',
          label: 'Plan',
          thClass: 'd-none d-md-table-cell',
          tdClass: 'align-middle d-none d-md-table-cell',
        },
        {
          key: 'billing_due',
          label: 'Billing Due',
          thClass: 'd-none d-lg-table-cell',
          tdClass: 'align-middle d-none d-lg-table-cell',
        },
        {
          key: 'last_activity',
          label: 'Last Activity',
          thClass: 'd-none d-lg-table-cell',
          tdClass: 'align-middle d-none d-lg-table-cell',
        },
        {
          key: 'health',
          label: 'Health',
          thClass: 'd-none d-lg-table-cell',
          tdClass: 'align-middle d-none d-lg-table-cell',
        },
        {
          key: 'onboarding',
          label: 'Onboarding',
          thClass: 'd-none d-lg-table-cell',
          thStyle: { minWidth: '130px' },
          tdClass: 'align-middle d-none d-lg-table-cell',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '48px' },
          tdClass: 'align-middle pr-2',
        },
      ];
    },

    allSelected() {
      return this.companies.length > 0 && this.companies.every(c => this.selectedIds.includes(c.id));
    },

    someSelected() {
      return this.selectedIds.length > 0 && !this.allSelected;
    },
  },

  watch: {
    dateRange() {
      this.fetchCompanies(1);
    },
  },

  mounted() {
    this.debouncedFetch = _.debounce(() => this.fetchCompanies(1), 400);
    this.fetchCompanies(1);
  },

  methods: {
    can(permission) {
      return dukakitCan(this.userDetails, permission);
    },

    async fetchCompanies(page = 1) {
      this.loading = true;
      try {
        const [sortField, sortDir] = (this.filters.sort || 'created_at:desc').split(':');
        const { data } = await axios.get('/api/admin/companies', {
          params: {
            page,
            per_page:  50,
            search:    this.filters.search || undefined,
            status:    this.filters.status || undefined,
            sort:      sortField,
            direction: sortDir || 'desc',
            show_test: this.filters.show_test ? 1 : 0,
          },
        });
        this.companies = data.data;
        this.meta = {
          current_page: data.current_page,
          last_page:    data.last_page,
          total:        data.total,
        };
      } catch (e) {
        // silent
      } finally {
        this.loading = false;
      }
    },

    onRowClicked(item) {
      this.$emit('company-selected', item);
    },

    rowClass(item, type) {
      if (type !== 'row') return '';
      return 'cursor-pointer' + (this.selectedIds.includes(item.id) ? ' table-active' : '');
    },

    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0][0].toUpperCase();
    },

    onboardingColor(company) {
      const pct = company.onboarding ? company.onboarding.percentage : 0;
      if (pct >= 90) return '#0f172a';
      if (pct >= 60) return '#64748b';
      return '#dc3545';
    },

    healthScore(company) {
      let score = 100;
      if (!company.billing) {
        score -= 20;
      } else if (moment(company.billing.due_date).isBefore(moment())) {
        score -= Math.min(45, moment().diff(moment(company.billing.due_date), 'days') * 3);
      }
      if (!company.user_activity_summary) {
        score -= 35;
      } else {
        const days = moment().diff(moment(company.user_activity_summary.updated_at), 'days');
        if (days > 90) score -= 40;
        else if (days > 30) score -= 20;
        else if (days > 14) score -= 8;
      }
      return Math.max(0, Math.min(100, score));
    },

    healthBadge(company) {
      const s = this.healthScore(company);
      if (s >= 80) return { label: 'Healthy', cls: 'badge-primary' };
      if (s >= 60) return { label: 'Good',    cls: 'badge-dark' };
      if (s >= 40) return { label: 'Fair',    cls: 'badge-secondary' };
      return              { label: 'At Risk', cls: 'badge-danger'  };
    },

    avatarColor(name) {
      const palette = [
        '#E53935','#D81B60','#8E24AA','#5E35B1',
        '#1E88E5','#00897B','#43A047','#F4511E',
        '#FB8C00','#F6BF26','#039BE5','#3949AB',
        '#00ACC1','#7CB342','#C0CA33','#6D4C41',
      ];
      if (!name) return palette[0];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return palette[Math.abs(hash) % palette.length];
    },

    companyStatus(company) {
      const days = moment().diff(moment(company.created_at), 'days');
      if (days <= 30) return 'trial';
      if (company.billing && moment(company.billing.due_date).isAfter(moment())) return 'active';
      return 'inactive';
    },

    isDuePast(company) {
      return company.billing && moment(company.billing.due_date).isBefore(moment());
    },

    statusLabel(company) {
      return { active: 'Active', trial: 'Trial', inactive: 'Inactive' }[this.companyStatus(company)];
    },

    statusBadgeClass(company) {
      return {
        active:   'badge-primary',
        trial:    'badge-dark',
        inactive: 'badge-secondary',
      }[this.companyStatus(company)];
    },

    formatDate(date) {
      return moment(date).format('D MMM YY');
    },

    fromNow(date) {
      return moment(date).fromNow();
    },

    refresh() {
      this.fetchCompanies(this.meta.current_page || 1);
    },

    async exportCsv() {
      this.exporting = true;
      try {
        const [sortField, sortDir] = (this.filters.sort || 'created_at:desc').split(':');
        const { data } = await axios.get('/api/admin/companies', {
          params: {
            per_page:  5000,
            search:    this.filters.search || undefined,
            status:    this.filters.status || undefined,
            sort:      sortField,
            direction: sortDir || 'desc',
            show_test: this.filters.show_test ? 1 : 0,
          },
        });
        const rows = data.data || [];
        const headers = ['Name', 'Email', 'Phone', 'Plan', 'Billing Due', 'Status', 'Health', 'Onboarding %', 'Created'];
        const lines = [headers.join(',')];
        rows.forEach(c => {
          const status = (() => {
            const days = moment().diff(moment(c.created_at), 'days');
            if (days <= 30) return 'Trial';
            if (c.billing && moment(c.billing.due_date).isAfter(moment())) return 'Active';
            return 'Inactive';
          })();
          const health = (() => {
            let score = 100;
            if (!c.billing) score -= 20;
            else if (moment(c.billing.due_date).isBefore(moment())) score -= Math.min(45, moment().diff(moment(c.billing.due_date), 'days') * 3);
            if (!c.user_activity_summary) score -= 35;
            else {
              const d = moment().diff(moment(c.user_activity_summary.updated_at), 'days');
              if (d > 90) score -= 40; else if (d > 30) score -= 20; else if (d > 14) score -= 8;
            }
            score = Math.max(0, Math.min(100, score));
            if (score >= 80) return 'Healthy';
            if (score >= 60) return 'Good';
            if (score >= 40) return 'Fair';
            return 'At Risk';
          })();
          lines.push([
            `"${(c.name  || '').replace(/"/g, '""')}"`,
            `"${(c.email || '').replace(/"/g, '""')}"`,
            `"${(c.phone || '').replace(/"/g, '""')}"`,
            `"${c.billing && c.billing.subscription_plan ? c.billing.subscription_plan.name : ''}"`,
            c.billing ? c.billing.effective_amount : '',
            c.last_paid_amount || '',
            `"${c.billing ? c.billing.due_date : ''}"`,
            status,
            health,
            c.onboarding ? c.onboarding.percentage + '%' : '',
            `"${moment(c.created_at).format('YYYY-MM-DD')}"`,
          ].join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `dukakit-companies-${moment().format('YYYY-MM-DD')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },

    copyEmail(company) {
      const email = company.email || (company.owner && company.owner.email) || '';
      if (!email) return;
      navigator.clipboard.writeText(email).then(() => {
        this.$emit('copied', email);
      });
    },

    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(idx, 1);
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds = [];
      } else {
        this.selectedIds = this.companies.map(c => c.id);
      }
    },

    clearSelection() {
      this.selectedIds = [];
    },

    bulkNotify() {
      this.bulkMessage = '';
      this.showBulkNotifyModal = true;
    },

    async submitBulkNotify() {
      if (!this.bulkMessage.trim()) return;
      this.bulkLoading = 'notify';
      try {
        const { data } = await axios.post('/api/admin/companies/bulk-action', {
          action:  'notify',
          ids:     this.selectedIds,
          message: this.bulkMessage.trim(),
        });
        this.$toasts.push({ message: data.message, type: 'success' });
        this.showBulkNotifyModal = false;
        this.clearSelection();
      } catch (e) {
        this.$toasts.push({ message: 'Bulk notify failed', type: 'error' });
      } finally {
        this.bulkLoading = null;
      }
    },

    async bulkArchive() {
      if (!confirm(`Archive ${this.selectedIds.length} companies? This is reversible.`)) return;
      this.bulkLoading = 'archive';
      try {
        const { data } = await axios.post('/api/admin/companies/bulk-action', {
          action: 'archive',
          ids:    this.selectedIds,
        });
        this.$toasts.push({ message: data.message, type: 'info' });
        this.clearSelection();
        this.fetchCompanies(this.meta.current_page || 1);
      } catch (e) {
        this.$toasts.push({ message: 'Bulk archive failed', type: 'error' });
      } finally {
        this.bulkLoading = null;
      }
    },

    bulkExport() {
      const selected = this.companies.filter(c => this.selectedIds.includes(c.id));
      const headers = ['Name', 'Email', 'Phone', 'Plan', 'Billing Due', 'Status', 'Health', 'Onboarding %', 'Created'];
      const lines = [headers.join(',')];
      selected.forEach(c => {
        const status = (() => {
          const days = moment().diff(moment(c.created_at), 'days');
          if (days <= 30) return 'Trial';
          if (c.billing && moment(c.billing.due_date).isAfter(moment())) return 'Active';
          return 'Inactive';
        })();
        lines.push([
          `"${(c.name  || '').replace(/"/g, '""')}"`,
          `"${(c.email || '').replace(/"/g, '""')}"`,
          `"${(c.phone || '').replace(/"/g, '""')}"`,
          `"${c.billing && c.billing.subscription_plan ? c.billing.subscription_plan.name : ''}"`,
          c.billing ? c.billing.effective_amount : '',
          c.last_paid_amount || '',
          `"${c.billing ? c.billing.due_date : ''}"`,
          status,
          this.healthBadge(c).label,
          c.onboarding ? c.onboarding.percentage + '%' : '',
          `"${moment(c.created_at).format('YYYY-MM-DD')}"`,
        ].join(','));
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `selected-companies-${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.admin-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
</style>
